import React, { useEffect, useState } from "react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";
import { Plus, CalendarBlank, Check, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPI = [
  { v: "analisi", label: "Analisi annuali" },
  { v: "audit", label: "Audit" },
  { v: "formazione", label: "Formazione" },
  { v: "altro", label: "Altro" },
];

const EMPTY = { cliente_id: "", tipo: "analisi", descrizione: "", data_scadenza: "", note: "", completato: false };

export default function Deadlines() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get("/deadlines"), api.get("/clients")]);
      setItems(d.data);
      setClients(c.data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    try {
      await api.post("/deadlines", {
        ...form,
        data_scadenza: new Date(form.data_scadenza).toISOString(),
      });
      toast.success("Scadenza creata");
      setAdding(false);
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  }

  async function toggle(d) {
    try {
      await api.put(`/deadlines/${d.id}`, { ...d, completato: !d.completato });
      await load();
    } catch (err) {
      toast.error("Errore");
    }
  }

  async function remove(d) {
    if (!window.confirm(`Eliminare "${d.descrizione}"?`)) return;
    try {
      await api.delete(`/deadlines/${d.id}`);
      await load();
    } catch (err) {
      toast.error("Errore");
    }
  }

  function clientName(id) {
    return clients.find((c) => c.id === id)?.ragione_sociale || "—";
  }

  function statusOf(d) {
    if (d.completato) return { label: "Completato", cls: "bg-green-100 text-green-800 border-green-300" };
    const days = Math.floor((new Date(d.data_scadenza) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: "Scaduta", cls: "bg-red-100 text-red-800 border-red-300", days };
    if (days <= 30) return { label: `${days}gg`, cls: "bg-amber-100 text-amber-800 border-amber-300", days };
    return { label: `${days}gg`, cls: "bg-gray-100 text-gray-800 border-gray-300", days };
  }

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Pianificazione</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Scadenze</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.filter(d => !d.completato).length} aperte · {items.filter(d => d.completato).length} completate</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="new-deadline-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          <Plus size={14} weight="bold" /> Nuova Scadenza
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#64748B]">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="swiss-card p-12 text-center">
          <CalendarBlank size={32} className="mx-auto text-[#64748B] mb-3" />
          <p className="text-sm text-[#64748B] mb-4">Nessuna scadenza programmata.</p>
          <button onClick={() => setAdding(true)} className="text-xs uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline">
            Aggiungi la prima scadenza
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => {
            const st = statusOf(d);
            return (
              <div key={d.id} className={`swiss-card p-4 flex items-center gap-4 ${d.completato ? "opacity-60" : ""}`} data-testid={`deadline-${d.id}`}>
                <button
                  onClick={() => toggle(d)}
                  data-testid={`toggle-deadline-${d.id}`}
                  className={`w-6 h-6 border-2 rounded-sm flex items-center justify-center flex-shrink-0 ${d.completato ? "bg-[#008A3C] border-[#008A3C]" : "border-[#E2E8F0] hover:border-[#0033A0]"}`}
                >
                  {d.completato && <Check size={14} weight="bold" color="#fff" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">{TIPI.find(t => t.v === d.tipo)?.label || d.tipo}</span>
                    <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className={`font-semibold mt-1 ${d.completato ? "line-through" : ""}`}>{d.descrizione}</div>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    {clientName(d.cliente_id)} · Scade il {formatDate(d.data_scadenza)}
                  </div>
                </div>
                {!d.completato && st.days < 0 && <Warning size={20} weight="fill" className="text-[#D32F2F]" />}
                <button onClick={() => remove(d)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-deadline-${d.id}`}>
                  <Trash size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAdding(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm max-w-md w-full p-6" data-testid="deadline-modal">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Nuova</div>
            <h2 className="font-display text-2xl font-bold mb-6">Scadenza</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Cliente *</label>
                <select required value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="deadline-client-select">
                  <option value="">— Seleziona —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none">
                  {TIPI.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Descrizione *</label>
                <input required value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="deadline-desc-input" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Data Scadenza *</label>
                <input type="date" required value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" data-testid="deadline-date-input" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Note</label>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setAdding(false)} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" className="btn-primary px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="deadline-save-btn">Salva</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
